import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { opsApi, apiError, fmtDate, fmtNum, GRAD, FONT_HEAD, Pagination } from './opsApi';

const STATUS_STYLES = {
  open: 'bg-blue-50 text-blue-600 border-blue-100',
  in_progress: 'bg-amber-50 text-amber-600 border-amber-100',
  resolved: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  closed: 'bg-gray-50 text-gray-600 border-gray-100',
};

const PRIORITY_STYLES = {
  low: 'bg-gray-50 text-gray-500 border-gray-100',
  medium: 'bg-amber-50 text-amber-500 border-amber-100',
  high: 'bg-orange-50 text-orange-500 border-orange-100',
  urgent: 'bg-red-50 text-red-500 border-red-100',
};

const TYPE_LABELS = {
  software_issue: 'Software',
  billing_question: 'Billing',
  account_issue: 'Account',
  feature_request: 'Feature',
  other: 'Other',
};

const VALID_TYPES = Object.keys(TYPE_LABELS);
const VALID_PRIORITIES = ['low', 'medium', 'high', 'urgent'];

function StatusBadge({ status }) {
  return (
    <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-bold border ${STATUS_STYLES[status] || STATUS_STYLES.open}`}>
      {(status || 'open').replace('_', ' ').toUpperCase()}
    </span>
  );
}

function PriorityBadge({ priority }) {
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${PRIORITY_STYLES[priority] || PRIORITY_STYLES.medium}`}>
      {(priority || 'medium').toUpperCase()}
    </span>
  );
}

function StatsBar({ stats }) {
  if (!stats) return null;
  const items = [
    { label: 'Total', value: stats.total, color: '#64748B' },
    { label: 'Open', value: stats.open, color: '#3B82F6' },
    { label: 'In Progress', value: stats.in_progress, color: '#F59E0B' },
    { label: 'Resolved', value: stats.resolved, color: '#10B981' },
    { label: 'Closed', value: stats.closed, color: '#9CA3AF' },
  ];
  return (
    <div className="grid grid-cols-5 gap-3 mb-6">
      {items.map(({ label, value, color }) => (
        <div key={label} className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="h-0.5 rounded-full mb-3" style={{ background: color }} />
          <div className="text-2xl font-bold tabular-nums" style={FONT_HEAD}>{fmtNum(value || 0)}</div>
          <div className="text-xs text-slate-400 mt-1">{label}</div>
        </div>
      ))}
    </div>
  );
}

export default function SupportTicketsPage({ user }) {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 25 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      if (priorityFilter) params.priority = priorityFilter;

      const [ticketsRes, statsRes] = await Promise.all([
        opsApi.get('/support-tickets', { params }),
        opsApi.get('/support-tickets/stats'),
      ]);
      setTickets(ticketsRes.data.items || []);
      setPagination(ticketsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(apiError(e));
    }
    setLoading(false);
  }, [page, search, statusFilter, typeFilter, priorityFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const exportCSV = () => {
    const headers = ['ID', 'Institution', 'Title', 'Type', 'Priority', 'Status', 'Created', 'Resolved'];
    const rows = tickets.map((t) => [
      t.id?.slice(0, 8).toUpperCase(),
      t.tenant_name || '',
      `"${(t.title || '').replace(/"/g, '""')}"`,
      t.type || '',
      t.priority || '',
      t.status || '',
      fmtDate(t.created_at),
      fmtDate(t.resolved_at),
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `support-tickets-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="support-tickets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={FONT_HEAD}>Support Tickets</h1>
          <p className="text-sm text-slate-400 mt-1">Manage all institution support requests</p>
        </div>
        <button
          onClick={exportCSV}
          className="h-9 px-4 text-xs font-semibold rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors"
          data-testid="export-csv-btn"
        >
          Export CSV
        </button>
      </div>

      <StatsBar stats={stats} />

      {/* Filters */}
      <form onSubmit={handleSearch} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, description, institution..."
            className="w-full h-9 px-3 text-sm rounded-lg border border-slate-200 bg-white"
            data-testid="ops-ticket-search"
          />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="h-9 px-3 text-sm rounded-lg border border-slate-200 bg-white"
            data-testid="ops-status-filter"
          >
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">Type</label>
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            className="h-9 px-3 text-sm rounded-lg border border-slate-200 bg-white"
            data-testid="ops-type-filter"
          >
            <option value="">All Types</option>
            {VALID_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">Priority</label>
          <select
            value={priorityFilter}
            onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }}
            className="h-9 px-3 text-sm rounded-lg border border-slate-200 bg-white"
            data-testid="ops-priority-filter"
          >
            <option value="">All Priority</option>
            {VALID_PRIORITIES.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
        </div>
        <button
          type="submit"
          className="h-9 px-4 text-xs font-semibold rounded-xl text-white transition-colors"
          style={{ background: GRAD }}
          data-testid="apply-filters-btn"
        >
          Search
        </button>
      </form>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: GRAD }} />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-16 text-sm text-slate-400">No tickets found</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    <th className="text-left px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">ID</th>
                    <th className="text-left px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Institution</th>
                    <th className="text-left px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Title</th>
                    <th className="text-left px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Type</th>
                    <th className="text-left px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Priority</th>
                    <th className="text-left px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="text-left px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Created</th>
                    <th className="text-left px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {tickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      className="hover:bg-slate-50/50 transition-colors"
                      data-testid={`ops-ticket-row-${ticket.id}`}
                    >
                      <td className="px-5 py-3.5 font-mono text-[11px] text-slate-400">
                        #{ticket.id?.slice(0, 8).toUpperCase()}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="font-medium text-slate-800 text-sm truncate max-w-[140px]">{ticket.tenant_name || '—'}</div>
                        {ticket.institution_code && (
                          <div className="text-[10px] text-slate-400 font-mono">{ticket.institution_code}</div>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="font-medium text-slate-800 max-w-[200px] truncate">{ticket.title}</div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 font-medium">
                          {TYPE_LABELS[ticket.type] || ticket.type}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <PriorityBadge priority={ticket.priority} />
                      </td>
                      <td className="px-5 py-3.5">
                        <StatusBadge status={ticket.status} />
                      </td>
                      <td className="px-5 py-3.5 text-xs text-slate-400">{fmtDate(ticket.created_at)}</td>
                      <td className="px-5 py-3.5">
                        <button
                          onClick={() => navigate(`/sales/support-tickets/${ticket.id}`)}
                          className="h-7 px-3 text-xs font-semibold rounded-lg text-white transition-colors"
                          style={{ background: GRAD }}
                          data-testid={`view-ticket-${ticket.id}`}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination data={pagination} onPage={(p) => setPage(p)} />
          </>
        )}
      </div>
    </div>
  );
}
